import { useNavigate } from "react-router-dom";
import { CheckCircle2, ExternalLink, ArrowLeft } from "lucide-react";

const steps = [
  {
    number: "01",
    title: "Enrol to Vote",
    color: "#2E5948",
    description:
      "You must be enrolled to vote in Australia. If you're 18 or over and an Australian citizen, it's both your right and legal obligation to enrol. Enrolment takes 2 minutes online.",
    tips: [
      "Check your enrolment at aec.gov.au",
      "Update if you've moved recently",
      "Enrolment closes 7 days before election day",
      "It's compulsory — failing to vote can result in a fine",
    ],
    link: { label: "Enrol at AEC", url: "https://www.aec.gov.au/enrol/" },
  },
  {
    number: "02",
    title: "Understand Preferential Voting",
    color: "#1B4F9E",
    description:
      "Australia uses preferential voting (or ranked-choice voting) for the House of Representatives. Instead of just one tick, you number every candidate from your most to least preferred.",
    tips: [
      "Number every box — leaving one blank makes the vote informal",
      'Write "1" for your top choice, "2" for second, and so on',
      "Your vote flows to your next preference if your first choice is eliminated",
      "This means your vote always counts, even if your favourite doesn't win",
    ],
    link: null,
  },
  {
    number: "03",
    title: "Senate: Above or Below the Line",
    color: "#7C3AED",
    description:
      "For the Senate, you have two options: vote Above the Line (quick — number at least 6 party boxes) or Below the Line (detailed — number at least 12 individual candidates).",
    tips: [
      "Above the line is simpler and most common",
      "Number at least 6 boxes above the line",
      "Below the line gives you more control over your preferences",
      "The Senate uses proportional representation — minor parties can win seats",
    ],
    link: null,
  },
  {
    number: "04",
    title: "On Election Day",
    color: "#C04E3F",
    description:
      "Polling places are open 8am to 6pm on election day. Bring your ID or know your full name and address. You'll receive a ballot for the House of Representatives and one for the Senate.",
    tips: [
      "Polling is compulsory — you must attend or apply for a postal vote",
      "Use a pencil or pen (both are accepted)",
      "Take your time — there's no rush",
      "You can vote early at pre-poll centres or by post",
    ],
    link: { label: "Find your polling place", url: "https://electorate.aec.gov.au/" },
  },
  {
    number: "05",
    title: "State & Territory Elections",
    color: "#D97706",
    description:
      "Each state and territory has its own electoral system and rules. Most use similar preferential voting, but the Senate equivalent (upper house) differs by jurisdiction.",
    tips: [
      "Check your state electoral commission for local rules",
      "NSW, VIC, QLD, WA, SA, TAS, ACT and NT all hold separate elections",
      "State elections choose your local MP and party for state parliament",
      "Some states use optional preferential voting — check your ballot",
    ],
    link: { label: "State commissions", url: "https://www.aec.gov.au/links/" },
  },
];

export default function HowToVotePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9F7F2" }}>
      {/* Page Header */}
      <div
        className="py-16 px-4 border-b-2 border-black"
        style={{ backgroundColor: "#0F172A" }}
        data-testid="how-to-vote-header"
      >
        <div className="max-w-4xl mx-auto">
          <button
            data-testid="back-home-btn"
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm font-bold mb-6 hover:opacity-80 transition-opacity"
            style={{ color: "#FFC82E" }}
          >
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
          <div
            className="inline-block px-3 py-1 mb-4 text-xs font-bold tracking-widest border-2 uppercase"
            style={{ borderColor: "#FFC82E", color: "#FFC82E" }}
          >
            Voter's Guide
          </div>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-4"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            How to Vote in Australia
          </h1>
          <p
            className="text-lg max-w-2xl"
            style={{ color: "rgba(255,255,255,0.8)", fontFamily: "Public Sans, sans-serif" }}
          >
            Everything you need to know about voting — from enrolling to filling in your ballot — explained clearly for first-timers and experienced voters alike.
          </p>
        </div>
      </div>

      {/* Steps */}
      <section className="py-14 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          {steps.map((step, idx) => (
            <div
              key={idx}
              data-testid={`voting-step-${idx + 1}`}
              className="bg-white border-2 border-black"
              style={{ boxShadow: "4px 4px 0px 0px #000000" }}
            >
              <div className="flex items-center gap-4 p-6 border-b-2 border-black">
                <div
                  className="w-14 h-14 flex items-center justify-center flex-shrink-0 border-2 border-black"
                  style={{ backgroundColor: step.color }}
                >
                  <span
                    className="text-xl font-black text-white"
                    style={{ fontFamily: "Outfit, sans-serif" }}
                  >
                    {step.number}
                  </span>
                </div>
                <h2
                  className="text-xl font-black"
                  style={{ fontFamily: "Outfit, sans-serif", color: "#0F172A" }}
                >
                  {step.title}
                </h2>
              </div>
              <div className="p-6">
                <p
                  className="text-base text-[#0F172A] mb-5 leading-relaxed"
                  style={{ fontFamily: "Public Sans, sans-serif" }}
                >
                  {step.description}
                </p>
                <ul className="space-y-2 mb-5">
                  {step.tips.map((tip, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2
                        className="w-4 h-4 mt-0.5 flex-shrink-0"
                        style={{ color: step.color }}
                      />
                      <span className="text-sm text-[#0F172A]" style={{ fontFamily: "Public Sans, sans-serif" }}>
                        {tip}
                      </span>
                    </li>
                  ))}
                </ul>
                {step.link && (
                  <a
                    href={step.link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`step-link-${idx + 1}`}
                    className="inline-flex items-center gap-2 px-5 py-2 text-sm font-bold border-2 border-black hover:-translate-y-0.5 transition-transform"
                    style={{ backgroundColor: step.color, color: "#fff", boxShadow: "2px 2px 0px 0px #000000" }}
                  >
                    {step.link.label} <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Quick Reference */}
      <section className="py-14 px-4" style={{ backgroundColor: "#2E5948" }} data-testid="quick-reference">
        <div className="max-w-4xl mx-auto">
          <h2
            className="text-2xl font-black text-white mb-6"
            style={{ fontFamily: "Outfit, sans-serif" }}
          >
            Key Electoral Commissions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { label: "Federal (AEC)", url: "https://www.aec.gov.au" },
              { label: "NSW Electoral Commission", url: "https://www.elections.nsw.gov.au" },
              { label: "Victorian Electoral Commission", url: "https://www.vec.vic.gov.au" },
              { label: "Electoral Commission Qld", url: "https://www.ecq.qld.gov.au" },
              { label: "WA Electoral Commission", url: "https://www.elections.wa.gov.au" },
              { label: "Electoral Commission SA", url: "https://www.ecsa.sa.gov.au" },
              { label: "Tasmanian Electoral Commission", url: "https://www.tec.tas.gov.au" },
              { label: "ACT Electoral Commission", url: "https://www.elections.act.gov.au" },
              { label: "NT Electoral Commission", url: "https://ntec.nt.gov.au" },
            ].map((item, i) => (
              <a
                key={i}
                href={item.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`commission-link-${i}`}
                className="flex items-center justify-between p-4 bg-white border-2 border-black font-bold text-sm hover:-translate-y-0.5 transition-transform"
                style={{ color: "#0F172A", boxShadow: "2px 2px 0px 0px #000000" }}
              >
                {item.label}
                <ExternalLink className="w-4 h-4 text-[#475569]" />
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Back CTA */}
      <div className="py-12 px-4 text-center border-t-2 border-black">
        <button
          data-testid="back-to-parties-btn"
          onClick={() => navigate("/")}
          className="px-8 py-3 text-white font-bold text-base border-2 border-black hover:-translate-y-1 transition-transform"
          style={{ backgroundColor: "#2E5948", boxShadow: "4px 4px 0px 0px #000000" }}
        >
          ← Browse Parties
        </button>
      </div>
    </div>
  );
}
